import { useRef } from 'react'
import { type AreaAssignInput, type AreaAssignSuccess, MINUTE, toResponseError } from '@terros/common'
import { useQueryClient } from '@tanstack/react-query'
import { useTantalimMutation } from '../mutation'
import { type AddHookResponse, mutationAdd } from '../hooks'
import { useAreaCache } from './cache'

const DELAYED_INVALIDATION_MS = MINUTE

export function useRunAreaAssignment(): AddHookResponse<AreaAssignInput, AreaAssignSuccess> {
  const cache = useAreaCache()
  const queryClient = useQueryClient()
  const invalidationTimerRef = useRef<ReturnType<typeof setTimeout>>()
  const mutation = useTantalimMutation<AreaAssignInput, AreaAssignSuccess>({
    url: '/area/assign',
  })

  function scheduleAssignmentRelatedInvalidation(): void {
    if (invalidationTimerRef.current) clearTimeout(invalidationTimerRef.current)
    invalidationTimerRef.current = setTimeout(() => {
      // Team-scoped area lists resolve through elastic, which lags the write.
      // Refetching immediately drops an area that was just created and assigned.
      cache.clearList()
      cache.clearSearchAccounts()
      queryClient.invalidateQueries({ queryKey: ['accounts'] })
      queryClient.invalidateQueries({ queryKey: ['accounts-infinite'] })
      queryClient.invalidateQueries({ queryKey: ['report-kpi'] })
      queryClient.invalidateQueries({ queryKey: ['report-user-kpi'] })
      queryClient.invalidateQueries({ queryKey: ['stats'] })
    }, DELAYED_INVALIDATION_MS)
  }

  async function assignAreas(input: AreaAssignInput): Promise<AreaAssignSuccess> {
    try {
      const result = await mutation.mutateAsync(input)
      for (const area of input.areas ?? []) {
        cache.clearArea(area.areaId)
      }
      scheduleAssignmentRelatedInvalidation()
      return result
    } catch (error) {
      throw toResponseError(error)
    }
  }

  return mutationAdd(mutation, assignAreas)
}
